/**
 * WALKTHROUGHS — the day, written down for the person living it.
 *
 * Anthony: "Create support forum walkthroughs with the top three options
 * in tile format being for a shop owner, service advisor or technician."
 *
 * Three tiles because there are three jobs in this shop, and a manual
 * written for everybody is read by nobody. The advisor does not care how
 * payroll closes; the technician does not care what the month looked like.
 * Each one is that person's whole day, in order, in the shop's own words.
 *
 * NOT a feature list. Staff training starts on the machine they will use,
 * so every step names the button they will actually press and, where it
 * matters, links straight to it. A walkthrough that cannot be followed
 * with one hand while holding a phone is a brochure.
 *
 * ─── AND THEN IT HAD TO COVER THE WHOLE THING. 2026-09-17 ──────────────
 *
 * Six tracks described a DAY and assumed the reader already knew what a
 * lead pile was, what the six tabs on a job were, and where the money
 * lived. Both guided walks open at "open a job" and never say where a job
 * comes from. A shop that signed up that morning had nothing to read, and
 * the commonest way a shop stops using software is that nobody ever told
 * them what the screen in front of them was for.
 *
 * So there are sixteen now, on four shelves, and between them they cover
 * every screen a shop touches: signing up, the leads, the job card, the
 * price and the yes, the board, the car, the money, the customer's own
 * page, the settings, the rules that do not bend and what to do when
 * something is broken.
 *
 * THE RULE THAT KEEPS IT HONEST has not changed and is the reason this is
 * a screen rather than a document somewhere: the step text here is the
 * wizard's own sentences, off src/domain/tracks.ts. A manual kept
 * somewhere else drifts from the software inside a month, and then it
 * teaches people things that are no longer true.
 */

import { esc, mount } from "../ui/dom.ts";
import { icon } from "../ui/icons.ts";
import { say, shopWords } from "../ui/words.ts";
import type { Role } from "../domain/permissions.ts";

/**
 * WHICH SCREEN THE PICTURES SHOW.
 *
 * Anthony: "take a screenshot walkthrough on mobile, tablet and desktop."
 *
 * The same panel is a different shape on a phone, and every layout failure
 * this shop has hit was a width failure -- Robert could not reach Save on
 * his Android, the lead sheet covered the page on a phone and nowhere
 * else. Somebody being trained on a phone should be looking at the phone.
 *
 * tools/help-shots.mjs writes name.png, name-t.png and name-m.png, and
 * skips the narrow copies of anything already narrower than the device. So
 * a missing file is normal and the img falls back to the plain name rather
 * than showing a broken picture.
 */
type Device = "" | "-t" | "-m";
const DEVICES: Array<{ tag: Device; label: string }> = [
  { tag: "-m", label: "Phone" },
  { tag: "-t", label: "Tablet" },
  { tag: "", label: "Desktop" },
];
let device: Device = "-m";

/**
 * THE PICTURE ON A TRACK, AND WHY IT IS THE PHONE ONE.
 *
 * These are the demo shop's own screens, off /images/walk/phone. Every
 * customer, car and price in them is invented, which is the only way a
 * picture is allowed to live in a manual that gets shown to new staff and
 * screenshotted again.
 *
 * Phone shots rather than desktop because that is what somebody being
 * trained is holding. The card crops from the top, so what shows is the
 * header and the first thing on the screen, which is the part that has to
 * be recognised.
 *
 * Not every track has one and that is deliberate. Settings, the rules and
 * the fix-it track are not a screen you look at. The card is built to be
 * right without a picture rather than to look like it lost one.
 */
const HERO: Record<string, string> = {
  start: "board-day",
  owner: "board-day",
  advisor: "jobs",
  tech: "job-sheet",
  guided: "job-sheet",
  intake: "text-desk",
  jobcard: "job-sheet",
  quoting: "quote",
  money: "payroll",
  board: "board-day",
  portal: "garage",
  comms: "text-desk",
};

/**
 * The three the manual was asked for in the first place. Anthony: "the top
 * three options in tile format being for a shop owner, service advisor or
 * technician." They are promoted OUT of their shelf rather than repeated
 * in it, so nothing on this page appears twice.
 */
const ROLE_TRACKS = ["owner", "advisor", "tech"];

type Step = {
  act: string; why?: string; go?: string;
  /** A picture from tools/help-shots.mjs, of the real screen. */
  shot?: string;
  /**
   * DROPPED ENTIRELY FOR A TRADE WITH NO ENGINES, rather than reworded.
   *
   * The answer line talks specs, tests and trouble codes to a working
   * mechanic. There is no locksmith wording for that step, because there
   * is no locksmith version of the thing -- so a lock shop reading a
   * reworded one would be told about a number that will not help them.
   * A manual describing a feature that is not yours is worse than a
   * manual that is one step shorter.
   */
  engineOnly?: true;
};
/** A picture of the real screen, rendered from the app's own markup. */
/**
 * TWO FOLDERS, BECAUSE THERE ARE TWO KINDS OF PICTURE.
 *
 * /images/help holds pieces of the app -- a card, a bar, a tile -- and its
 * device variants are `-t` and `-m` off a desktop original.
 *
 * /images/walk holds whole wizard steps, drawn at a phone width FIRST
 * because that is where a wizard is used, with `-t` and `-d` for the iPad
 * on the bench and the laptop at the counter. A step is named `walk:01-start`.
 */
const shotOf = (name: string, alt: string) => {
  const walk = name.startsWith("walk:");
  const file = walk ? name.slice(5) : name;
  const dir = walk ? "walk" : "help";
  /* The walk shots are phone-native, so the plain name IS the phone one
     and the desktop variant is -d. The help shots are the other way up. */
  const tag = walk
    ? (device === "-m" ? "" : device === "-t" ? "-t" : "-d")
    : device;
  return `
  <figure class="wt-shot wt-shot${esc(device || "-d")}">
    <img loading="lazy" src="/images/${dir}/${file}${tag}.png" alt="${alt}"
         onerror="this.onerror=null;this.src='/images/${dir}/${file}.png'">
  </figure>`;
};

/**
 * FOUR SHELVES, BECAUSE SIXTEEN TILES IN ONE GRID IS A WALL.
 *
 * The manual grew from six tracks to sixteen when it was made to cover the
 * whole product rather than the two guided walks. Sixteen equal tiles is a
 * list you scroll past, so they are shelved by WHEN you need them: the day
 * you sign up, the day you work, the screen you are standing in front of,
 * and the day something is wrong.
 *
 * Order inside a shelf is the order of TRACKS, so moving a tile is moving
 * its entry rather than editing a second list that would drift from this
 * one.
 */
const SHELVES = ["Start here", "Your day", "The screens", "When it matters"] as const;
type Shelf = (typeof SHELVES)[number];

type Track = {
  key: string; role: string; who: string; icon: string; tint: string;
  /** Which shelf it sits on. */
  group: Shelf;
  opens: string; steps: Step[]; traps: string[];
  /**
   * WHO THIS ONE IS FOR.
   *
   * Anthony: "Corresponding roles should have access to the parts that
   * apply to what they do."
   *
   * A technician handed the payroll walkthrough learns that this manual is
   * mostly not about them, and stops opening it. Whoever runs the shop
   * sees everything, because they are doing all of it.
   */
  roles: Role[];
};

const DESK: Role[] = ["advisor", "admin", "supervisor"];
const FLOOR: Role[] = ["technician", "admin", "supervisor"];
const RUNS_IT: Role[] = ["admin", "supervisor"];
const EVERYONE: Role[] = ["technician", "advisor", "admin", "supervisor", "viewer"];
/** Anybody who can touch money on a job. A viewer cannot. */
const HANDS_ON: Role[] = ["technician", "advisor", "admin", "supervisor"];

/** The tracks this person is meant to read. */
export const tracksFor = (role: Role): Track[] =>
  TRACKS.filter((t) => t.roles.includes(role));

export const TRACKS: Track[] = [
  /**
   * THE FIRST TEN MINUTES.
   *
   * Every other track assumes you already know what you are looking at.
   * Somebody who signed up an hour ago does not, and the commonest way a
   * shop stops using software is that nobody ever told them what the
   * screen in front of them was FOR.
   *
   * Short on purpose. Six things, none of which need a customer, so it can
   * be read before the first car arrives.
   */
  {
    key: "start", role: "Your first ten minutes", who: "You just signed up",
    icon: "manuals", tint: "green", group: "Start here", roles: EVERYONE,
    opens: "Anywhere. Nothing here needs a customer, {acar} or a price.",
    steps: [
      { act: "Sign in, and if the screen is empty, say so.", shot: "signin",
        why: "One form does both jobs, signing in and making a login. There is a third state people hit and it looks broken. Signed in, but not attached to a shop yet. Every list reads empty. That is not a fault, it means whoever runs the shop has not added you on Team yet.",
        go: "#/" },
      { act: "Look at the bar. There are six places and Desk is in the middle.", shot: "nav",
        why: "Leads, Quotes and Jobs on one side, because that is the order work moves in. Schedule, Customers and More on the other, because they support it. Desk sits in the middle rather than at one end so it is the same thumb travel home from either side of a phone." },
      { act: "Start every day on the Desk.",
        why: "One line of numbers across the top, one stop per stage, and under each one how much of it has been sitting too long. Tap a number and you get the list behind it. You are not hunting a calendar to find out what is going wrong today.",
        go: "#/", shot: "on-you" },
      { act: "Know {thecar} by its picture.", go: "#/jobs", shot: "jobs-list", engineOnly: true,
        why: "Jobs, quotes and the Desk show a small photo of {thecar}, the first one taken when it arrived. No photo yet shows a picture of that model instead, and a car nobody can match shows the coin. Take the arrival photo on every job, and every list after that tells you which car it is before you read a word." },
      { act: "Fill in your own page.",
        why: "Your experience and your certifications, in your own words. It is what answers the question of who should take this job, and a tick box skills list never does. You write your own. Nobody writes it for you.",
        go: "#/me", shot: "me" },
      { act: "Learn the two cards above the tabs on a job.", shot: "walkdoors",
        why: "Green means there is work {onsite}. Blue means there is work at the desk. If you run the shop and both have something in them you get both, framed in gold. Those two cards are how the work actually gets done in here, and the next tracks are about them." },
      { act: "If a screen ever goes blank or a phone gets stuck, there is one button.", shot: "stopped",
        why: "Clear this device and start again. It wipes what is saved on the phone and takes you back to signing in. Nothing is lost, because nothing lives on the phone. Everything is on the server." },
    ],
    traps: [
      "Every list empty on your first day almost always means you are signed in but not attached to a shop yet. Ask whoever runs it to add you on Team.",
      "The theme switch is remembered on that browser, not on you. Your phone can be dark and the counter laptop light.",
      "Nothing you do in here is saved on the device. Losing a phone loses a phone, not a day's work.",
    ],
  },
  {
    key: "owner", role: "Shop owner", who: "You own the numbers", icon: "desk", tint: "purple",
    group: "Your day",
    roles: RUNS_IT,
    opens: "The dashboard, every morning, before anybody asks you anything.",
    steps: [
      { act: "Read the On you card under the railway before anything else.",
        why: "Everything with your name on it, most recent first, and what you finished this week underneath in green. It shows a week. Older is behind a button that says how many.",
        go: "#/", shot: "on-you" },
      { act: "Open the dashboard and read the railway left to right.", shot: "railway",
        why: "Each stop is a count of real jobs at that stage. A stop fatter than the one after it is where work is piling up.", go: "#/" },
      { act: "Check what fell off it.", shot: "fell",
        why: "Leads with no quote, jobs finished but never billed, work that was lost. This is the money that walks out quietly.", go: "#/leads" },
      { act: "It opens on this month. Set it to the week and compare.",
        why: "One slow day is weather. One slow month is a problem. It opens on the month because that is the window where something can be stuck. A quote is stuck after a week, and a week holds none that old." },
      { act: "Open Invoices and look at what is owed.", shot: "owed",
        why: "Anything past a fortnight wants a phone call, not another email.", go: "#/invoices" },
      { act: "Skim Authorizations for anything spoken rather than signed.", shot: "authorizations",
        why: "A spoken yes is valid, and it is also the one you will be asked to prove. Know which jobs are carrying them.", go: "#/authorizations" },
      { act: "Check Team before you hire, and again before payday.",
        why: "Who can sign in, and at what level. A person who left still holding a login is the most ordinary way a shop gets robbed. Each card also says what that person finished and what is still on them.",
        go: "#/team", shot: "team-score" },
      { act: "Set your labor rate and shop details once, in Settings.", shot: "settings",
        why: "Every quote from then on prices itself off that number.", go: "#/settings" },
    ],
    traps: [
      "The dashboard counts each stage by when THAT stage happened, not when the job was created. A job booked today shows in Booked today even if the call came in last month.",
      "You can close a job that failed its checks. It is recorded with your name and your reason, and it should stay rare.",
    ],
  },
  /**
   * THE SERVICE ADVISOR'S WALK, step by step.
   *
   * Anthony, 2026-09-09: "the docs we made for walkthroughs with
   * screenshots for advisor and tech ... what's currently there is old and
   * outdated."
   *
   * It was. The old track described a flow that predates the two guided
   * walks: open the bell, work the leads screen, record the authorization
   * on the authorizations page. All of that still exists and none of it is
   * how a job is worked now. The steps below are the eight in
   * src/domain/tracks.ts, in their order, in the wizard's own words -- so
   * the manual cannot drift from the screen, because they are the same
   * sentences.
   */
  {
    key: "advisor", role: "Service advisor", who: "You own the customer",
    group: "Your day",
    icon: "customers", tint: "green", roles: DESK,
    opens: "Any job with a blue card above the tabs. Eight steps, and the ones that fit in one field are done right there.",
    steps: [
      { act: "Open the job and tap the blue card.",
        why: "Blue is always the desk. It says the next real thing and how many steps are left, so you can decide whether to open it without opening it. No card means this half is finished.",
        go: "#/quotes" },
      { act: "Step 1 \u2014 who and what.",
        why: "Their name, a number, where {thecar} is, what it is, and what it is doing. Nothing here stops you. It is what a price is built from, and anything missing is what gets argued about later. An in-shop job answers 'where' from your shop profile, so you do not type an address onto every job.",
        shot: "walk:a1-who" },
      { act: "Step 2 \u2014 price it.",
        why: "Lines with money on them. Put what the shop PAID in the cost box and the charge works itself out from the markup bands. The wizard shows the total and opens the real line editor when you need it, because a wizard is not a line editor and pretending otherwise gets you a worse one.",
        shot: "walk:a2-price" },
      { act: "Step 3 \u2014 send it to them.",
        why: "This is the button that used to be three quarters of the way down the job card. It sends the figure and a link to their own page where they can read what is on it and approve it, and starts a thirty day clock on the price. No mobile number and it says so, instead of failing on the tap.",
        shot: "walk:a3-send" },
      { act: "Step 4 \u2014 the yes.",
        why: "Signed on their own screen, or told to you on the phone and written down here. Either counts. Both go on the record with who agreed, to what, and when. One signature approves the price AND authorizes the work, so there is not a second ask.",
        shot: "walk:a4-yes" },
      { act: "Step 5 \u2014 book it in.",
        why: "A day and a time, typed here. Nothing is booked before there is a yes. A booking before an authorization is a promise the shop cannot keep, and the step says so rather than letting you past it.",
        shot: "walk:a5-book" },
      { act: "Step 6 \u2014 put somebody on it.",
        why: "Even if that somebody is you \u2014 there is a button for exactly that. A job with no name on it is a job nobody is expecting.",
        shot: "walk:a6-who-goes" },
      { act: "Step 7 \u2014 bill it.",
        why: "The total comes off the lines and nobody types it. Raising the invoice sends nothing to the customer. It is the bill existing so it can be paid. It will not bill work that is not finished.",
        shot: "walk:a7-bill" },
      { act: "Step 8 \u2014 take the money.",
        why: "Card, cash, check or transfer. Type a smaller figure to take part of it \u2014 a deposit, or just the diag \u2014 and it tells you what is still owed. A settled invoice closes the job by itself.",
        shot: "walk:a8-money" },
    ],
    traps: [
      "Billing and payment are on the {tech}'s walk too. Whoever gets there first does them, and the other walk then shows them done.",
      "A warranty or goodwill job. Set Not being billed on the job card first, and price, send, the yes, the bill and the money all step aside and say so.",
      "The walk never sends you back to a step you are plainly past. A job that is priced, sent and agreed will not open on step one because a mobile number is missing \u2014 that is still owed, and still on the card, but it is not where you are sent.",
      "A payment recorded twice can be taken off on the job card, with a reason. It is struck through rather than deleted, and the job comes back out of paid on its own.",
    ],
  },
  /**
   * THE TECHNICIAN'S WALK, step by step. The ten in src/domain/tracks.ts
   * and src/pages/tech.ts, with the screens from tools/walk-shots.mjs.
   * Replaces a track that described the old job card and never mentioned
   * the wizard at all.
   */
  {
    key: "tech", role: "{Tech}", who: "You own {thecar}",
    group: "Your day",
    icon: "jobs", tint: "amber", roles: FLOOR,
    opens: "Today's job with a green card above the tabs, or the Start link on the jobs list.",
    steps: [
      { act: "Open the job and tap the green card.",
        why: "Green is always {thecar}. It carries on where you left it \u2014 nothing about how far you got is stored anywhere, because the JOB is the progress. Put the phone down at lunch and come back to the same place.",
        go: "#/schedule" },
      { act: "Step 1 \u2014 start the job.",
        why: "The yes on file{miles} and one photo of how it turned up. The photo gates the start rather than being a step you get past: {acar} whose arrival was never photographed is {thecar} you cannot defend three months later.",
        shot: "walk:01-start" },
      { act: "If nobody has said yes, write it down right there.",
        why: "No work starts without an authorization and that has not changed. What changed is that a yes given on the phone or {onsite} can be recorded without leaving this screen. No signature on a phone call \u2014 who agreed and when is the record.",
        shot: "walk:01b-no-yes" },
      { act: "Step 2 \u2014 walk {thecar} before anything comes apart.",
        why: "The inspection is its own screen because it is long. This step exists so it does not get skipped.",
        shot: "walk:02-inspect" },
      { act: "Step 3 \u2014 what you found.",
        why: "What is wrong with it, in your own words, and the photos. This is what the desk reads when the customer rings, and what justifies the hours.",
        shot: "walk:03-found" },
      { act: "Step 4 \u2014 is it bigger than the quote?",
        why: "If it is, you stop here and the desk gets a new yes. Carrying on past the authorized figure is the one thing that turns a good job into a complaint.",
        shot: "walk:04-money" },
      { act: "Steps 5 and 6 \u2014 do the repair, and shoot it after.",
        why: "Keep shooting as you go.{oldpart} These are what the customer sees on their own page and what settles an argument six months later.",
        shot: "walk:05-repair" },
      { act: "Step 7 \u2014 what identifies it.",
        why: "{identify}{vin}",
        shot: "walk:07-car" },
      { act: "Step 8 \u2014 mark it finished.",
        why: "That is the desk's signal to bill it. Until you do it looks unfinished to everybody. If something is missing the screen names it, and there is no button that makes a check go away for you.",
        shot: "walk:08-done" },
      { act: "Steps 9 and 10 \u2014 bill it and take the money, if you are the one holding the keys.",
        why: "In a one-man shop that is you. In a shop with a desk it means you are not stood waiting for the office to raise an invoice before the customer can pay you.",
        shot: "walk:09-bill" },
    ],
    traps: [
      "You cannot change a customer's phone number. That one is the desk's, because a wrong digit loses a customer silently and nobody finds out for a week.",
      "Put your hours on the job. That is what payroll reads, and a job with no hours is a job you were not paid for.",
      "Texting the desk number files photos, hours and questions against the job you are on. Nothing you send it reaches the customer.",
      "A step that says Not yet is not nagging. Read the line under it. It says what has to happen first, and it will skip you to the first thing you actually can do.",
    ],
  },
  /**
   * THE GUIDED WALKS, written down. Shipped 2026-09-09; see
   * src/domain/tracks.ts for the rules these steps obey.
   */
  {
    key: "guided", role: "The guided walks", who: "Two doors into one job",
    /* Start here rather than Your day. With the owner, the advisor and the
       technician promoted to cards of their own, Your day was a heading
       over one tile, and the guided walks are what a new person is sent
       through first anyway. */
    group: "Start here",
    icon: "manuals", tint: "green", roles: EVERYONE,
    opens: "Any job. The doors are above the tabs, and only when there is something behind them.",
    steps: [
      { act: "Open a job and look above the tabs.", shot: "walkdoors",
        why: "A green card means there is work {onsite}. A blue card means there is work at the desk. If you run the shop and both have something left, you get both, framed in gold." },
      { act: "Read the card before you tap it.",
        why: "It says the next real thing -- Send it to them, Book it in -- and how many steps are left. That is enough to decide whether to open it without opening it." },
      { act: "Tap the blue one for the desk half.",
        why: "Who and what, price it, send it, the yes, book it, who is going, bill it, take the money. Eight steps, and the ones that fit in one field are done right there." },
      { act: "Tap the green one for {thecar} half.",
        why: "Start it, inspect it, what you found, is it bigger than the quote, do the repair, photos after, {theplate}, mark it finished, bill, paid." },
      { act: "Put the phone down and come back.",
        why: "Nothing about how far you got is stored anywhere, because there is nothing to store. A step is finished when the JOB says so, so you land back exactly where the work actually is." },
      { act: "When a step says Not yet, read why.", shot: "blocked",
        why: "A blocked step is not a nag. No yes on file means the booking cannot happen, because a booking before an authorization is a promise the shop cannot keep. The walk skips you to the first thing you can actually do." },
      { act: "For a warranty or goodwill job, set Not being billed on the job card first.",
        why: "Price, send, the yes, the bill and the money all step aside and say so, instead of the walk quietly nagging for a price that is never coming." },
    ],
    traps: [
      "The doors disappear when a track is finished. That is not a bug -- a door into an empty room is how people learn to stop opening doors.",
      "The walk will not start work with no authorization on file. It offers to record the yes right there rather than letting you past it.",
      "Billing and payment are on BOTH walks. Whoever gets there first does them, and the other walk then shows them done.",
    ],
  },
  /**
   * ─── THE SHELF THE MANUAL WAS MISSING ──────────────────────────────────
   *
   * The three role tracks describe a DAY. They assume the reader already
   * knows what a lead pile is, what the six tabs on a job are, and where
   * the money lives. A shop that signed up this morning knows none of it,
   * and the two guided walks start at "open a job" without ever saying
   * where a job comes from.
   *
   * So these seven cover the SCREENS, one each, in the order a job crosses
   * them. Every fact in them is read off the screen it describes rather
   * than remembered, and where a rule is enforced the step says so, because
   * a person who knows why does the right thing next time unprompted.
   */
  {
    key: "intake", role: "Where the work comes from", who: "You own the first answer",
    icon: "leads", tint: "green", group: "The screens", roles: DESK,
    opens: "Leads, and the plus button on any list. Every job in here started as one of those two.",
    steps: [
      { act: "Read the three piles.", go: "#/leads", shot: "lead-tile",
        why: "Waiting, booked, lost. They are the funnel and not filters, so a lead is always in exactly one of them. After seven days of silence with no quote raised it moves itself to lost, whether or not anybody pressed anything." },
      { act: "Tap a card rather than scanning a row.",
        why: "A job row is a record you scan. A lead is a person you are about to ring, and their number, what they want and how long they have waited do not fit on one line without turning into a wall of small gray text. The card opens into the whole enquiry and the three things you can do about it." },
      { act: "Ring them back in the same hour.",
        why: "The lead moves from new to contacted the moment a person reaches out, and every card says whether anybody has. The first shop to answer usually gets the job, and this screen exists so nobody has to remember who is owed a call." },
      { act: "Write down what they told you, as a note.",
        why: "Notes live on a lead, a customer and a job, and they are the same card in all three places. Every one records who wrote it and when, to the minute, because a note is the thing somebody may be asked to account for later." },
      { act: "Press Raise a quote once it is real.",
        why: "It is the first button on the lead's sheet, and once pressed it reads Open the quote. It makes a job at estimate status with the enquiry attached, so the conversation and the price end up on one record instead of two." },
      { act: "When somebody rings and there is no lead, press the plus.", shot: "newjob",
        why: "One sheet, three fields. A name, a number, and what they want. That is what you have while the phone is still at your ear, and asking for {aserial} before anything can be written down is how the note ends up on paper instead." },
      { act: "Let it find the customer for you.",
        why: "It matches on the number before it creates anything, so a regular ringing for the fourth time gets a fourth job on the record they already have rather than a fourth copy of themselves. That is the duplicate problem being prevented rather than cleaned up later." },
      { act: "Search from any list instead of scrolling.", shot: "typeahead",
        why: "The find bar takes a name, a phone number or a job number, and it is on every list." },
    ],
    traps: [
      "A lead with nobody on file yet cannot be texted from the lead sheet. Make the customer first, then text.",
      "A lead that closed itself is not deleted. If they ring back a week later, open it and carry on.",
      "Leads arrive from the missed call textback, the website forms, the tracking numbers and the published inboxes. If one channel goes quiet, look at its switch on Automations before you go looking for a fault.",
    ],
  },
  {
    key: "jobcard", role: "The job card", who: "Everything about one job",
    icon: "jobs", tint: "amber", group: "The screens", roles: EVERYONE,
    opens: "Any job. Five green checks, one next action, six tabs, and the photos above all of them.",
    steps: [
      { act: "Read the five checks first.", shot: "stars",
        why: "Called, Quoted, Approved, Done, Paid. The same green checks the customer sees on their own page. Anybody can tell where a job stands from across the room without reading a word of it. Approved is lit by a yes on file, signed or spoken, and if it is dark the work is not yours to start." },
      { act: "Then read the one line under them.",
        why: "It says the single next thing this job wants, and why, and gives you the button for it. One next action on purpose. A card offering six equally weighted buttons is every option present and no path obvious, which is the thing this whole system was rebuilt to get rid of." },
      { act: "Use On my way when you set off.",
        why: "It tells the customer and changes nothing about the job, which is why it sits beside the next action rather than in the row of stages. It can never be the wrong button to press." },
      { act: "The six tabs are in the order a job goes through.",
        why: "Details, The work, {Thing}, Money, Comms, History. The tabs never move, so your thumb learns where they are, but which one opens changes with the job. A quote opens on the work, because the work is what a quote is." },
      { act: "Photos sit under the tabs and above every panel.",
        why: "A photo is about the job, not about one section of it, so it is on every tab rather than hidden behind one." },
      { act: "Tap a field to change it.",
        why: "Everything editable is edited where it is shown. If a field refuses you, the reason is written next to it. A {tech} can fix an address because they are standing in front of the house, and cannot change a phone number because that is how the shop reaches somebody and a wrong digit loses a customer silently." },
      { act: "Put the {thing} on its own tab.", shot: "vehicle-tab",
        why: "What it is{reading}. {identify}{vin}" },
      { act: "Say who is on it.", shot: "whobar",
        why: "The bar at the top carries the advisor and the crew. A job with no name on it is a job nobody is expecting, and there is a button that puts you on it in one tap." },
      { act: "History is where what happened is kept.",
        why: "Every stage move, every authorization, and every override with the name of whoever stepped past a check and the reason they gave." },
    ],
    traps: [
      "A quote and a job are the same record. The only difference is the status, which is why the same card serves both.",
      "An authorization taken over the phone is a real authorization and shows as one. The old system tested for a signature image and nothing else, so phone jobs read as unauthorized forever.",
      "Cancelling is not a stage. It ends the job, and who may do it is not the same question as who may move it along.",
    ],
  },
  {
    key: "quoting", role: "Pricing and the yes", who: "You own the number",
    icon: "quotes", tint: "green", group: "The screens", roles: DESK,
    opens: "The work tab of a job, and steps two, three and four of the blue card.",
    steps: [
      { act: "Know the six things a quote needs.",
        why: "Their name, a number, where {thecar} is, when they are free, what they say is wrong, and {thecar} to at least {ymm}. You can draft with less than that. The six gate SENDING, and the draft says in a plain sentence what is still missing." },
      { act: "Decide which of the two kinds of quote this is.",
        why: "Known work, {knownwork}, price the repair. {unknownwork}, price ONE HOUR OF DIAGNOSIS and nothing else. You cannot price a repair you have not identified, and a guess you have to walk back costs more than the hour ever did." },
      { act: "Add each line to its own group.", shot: "lines",
        why: "Labor, Parts and Fees each have their own + Add in their heading, so a part goes in with the parts. The total is the green bar under them. Book time and the prices you quoted before are in the small Fill in from row. Draft it for me writes a first quote when there is nothing on it yet, and folds away once there is." },
      { act: "Put in what the shop paid, not what the customer pays.",
        why: "Type the cost and the customer number works itself out from the markup bands, twelve of them, because a twenty dollar {part} and a nine hundred dollar {part} do not carry the same margin. Then change it if you want to. The bands cannot know this was {lastpart}." },
      { act: "Remember the customer never sees your cost.",
        why: "Cost is recorded so the shop knows what it made. It stays on your side of the screen and it does not cross to theirs, on the quote, on the invoice or on their own page." },
      { act: "Set the labor rate once.", go: "#/settings",
        why: "Every quote raised from then on prices its labor off that number. Changing it later does not reprice quotes already out, and that is deliberate. A price you sent is a price you sent." },
      { act: "Keep fees out of labor.",
        why: "A line recorded as labor pays commission on it. Storage, shop fees and the like have to be recorded as fees, or a storage charge quietly pays somebody a commission it was never meant to." },
      { act: "Send it, and watch the clock start.", shot: "walk:a3-send",
        why: "It sends the figure and a link to their own page where they can read it and approve it, and it starts a thirty day clock on the price. If there is no mobile number it says so before you tap, instead of failing on the tap." },
      { act: "Let it do the chasing.",
        why: "A sent quote asks once a day, four times at most, and stops the moment they say anything at all. Chasing by hand on top of that sends them two, which is how a shop reads as desperate." },
      { act: "Take the yes, however it arrives.", shot: "walk:a4-yes",
        why: "Signed on their own screen, or spoken on the phone and written down here with their name on it. Both count and both go on the record with who agreed, to what, and when. One signature approves the figure AND authorizes the listed work, and they are told that above the pad, so there is never a second ask." },
      { act: "If the price changes, get another yes.",
        why: "What they agreed to is frozen at the moment they agreed to it, not whatever the quote says next Tuesday. A second yes at a revised figure is expected. A second one at the same figure is refused, because two prices is what makes two answers necessary." },
      { act: "Work the pile rather than your memory.", go: "#/quotes", shot: "quote-piles",
        why: "Drafted, sent, answered, approved, expired, lost. The screen filters on them, and a quote nobody answered in a month closes itself. Reopen it in one tap if they ring." },
    ],
    traps: [
      "A quote with nothing priced on it cannot be sent, and the step says so rather than letting you past it.",
      "For a warranty or a goodwill job, set Not being billed on the job card first. Price, send, the yes, the bill and the money all step aside and say why.",
      "A diagnosis job comes back round for its real quote and its real yes. That loop is the normal case, not a mistake somebody made.",
    ],
  },
  {
    key: "board", role: "The board and the day", who: "You own where everybody is",
    icon: "schedule", tint: "purple", group: "The screens", roles: EVERYONE,
    opens: "Schedule. One control, three ranges, and any date is one tap from its own day.",
    steps: [
      { act: "Choose the range, not a different screen.", go: "#/schedule", shot: "schedule-bar",
        why: "Day, week or month is one control. Tapping any date anywhere sets the range to Day on that date, so drilling in is the same control landing on a smaller range rather than a mode you have to find your way back out of." },
      { act: "Remember nothing lands here until there is a yes.",
        why: "Approving a quote does not book it. Booking is its own step with a day and a time on it, because a booking made before an authorization is a promise the shop cannot keep." },
      { act: "Trust that a booked job has a date.",
        why: "The database refuses a booked job with no date, and the board asks the question again every time it loads, because impossible is a claim and this is the evidence. A job like that would sit on no board, appear on no map and fire no reminder." },
      { act: "Put somebody on every job.",
        why: "Even if that somebody is you. There is a button for exactly that, and a job with nobody on it is a job nobody is expecting." },
      { act: "Read today on the map.", go: "#/",
        why: "Today's work as pins. Mobile jobs sit at the customer town, shop jobs sit at the shop, because in the shop is the most precise address on the record rather than a missing one. It says towns and not doorsteps out loud, and anything it could not place is counted rather than guessed at." },
      { act: "Let the two reminders land apart.",
        why: "The {tech} is told an hour before and the customer forty five minutes before, so the two never arrive together and nobody is confirming a time to a person who has not been told it yet." },
    ],
    traps: [
      "The desk counts each stage by when THAT stage happened. A job booked today shows in Booked today even if the call came in last month.",
      "Moving a date does not text anybody by itself. Tell them.",
      "A job with no date is not today's work and will not be on the map, however urgent it is.",
    ],
  },
  {
    key: "car", role: "Photos and the inspection", who: "You own the evidence",
    icon: "camera", tint: "amber", group: "The screens", roles: FLOOR,
    opens: "A job you are standing at, on a phone, one thumb.",
    steps: [
      { act: "Shoot it before you touch it.", shot: "walk:01-start",
        why: "One photo of how it turned up, and it gates the start rather than being a step you get past. A {thing} whose arrival was never photographed is the one you cannot defend three months later when somebody says it was like that when I brought it in." },
      { act: "Use the button, not your camera roll.",
        why: "On a phone it opens the back camera straight into the shutter. One tap from the job to a photo, and it lands on the job where the desk and the customer can see it. A photo in your own camera roll exists nowhere as far as this shop is concerned." },
      { act: "Walk {thecar} before anything comes apart.", shot: "walk:02-inspect",
        why: "Sections you open one at a time with a count on each, so you can see what is left without scrolling the whole sheet. Every control is a target a thumb can hit, and nothing needs a keyboard until a note is owed." },
      { act: "Do not look for a Save button.",
        why: "There is not one, because there is no moment where the work should exist only on the screen. A sheet that needs two hands is a sheet that gets filled in afterwards from memory, and that is worse than no sheet because it looks like evidence." },
      { act: "Write what you found, not what it looks like.", shot: "walk:03-found",
        why: "One box, your own words, and the photos beside it. This is what the desk reads back to the customer on the phone, and what justifies the hours on the bill." },
      { act: "Found more later? Add it to the diagnostic card.",
        why: "Open the job and tap Diagnostic card. It is the same words you wrote in the walk, and anything you add saves as you type. The desk sees who wrote it and when. The customer reads it on their own page too, without your name, so write it for them. Hide from the customer keeps a card off their page." },
      { act: "Keep shooting through the repair.", shot: "walk:05-repair",
        why: "The photos taken halfway through are the ones nobody thinks to take and everybody wants later." },
      { act: "Shoot the finish.{oldpart}", shot: "walk:06-after",
        why: "This is what the customer opens their own page to look at, and it ends an argument before it starts." },
      { act: "Read what identifies it, while you are standing next to it.", shot: "walk:07-car",
        why: "{identify}{vin}" },
      { act: "Put a photo on a note when words will not do it.",
        why: "Notes take photos too, on a lead, a customer or a job, and every one is stamped with who wrote it and when, to the minute." },
    ],
    traps: [
      "A receipt photo never crosses to the customer page. It carries what the shop paid, and that is the one thing they must not see.",
      "The shop's whole roll is at More then Photos if you need to find a shot from a job last month.",
      "Photos are what the customer actually opens their page for. A job with none looks to them like a job nobody touched.",
    ],
  },
  {
    key: "money", role: "Billing, payment and payroll", who: "You own what the shop gets paid",
    icon: "invoices", tint: "purple", group: "The screens", roles: HANDS_ON,
    opens: "The Money tab of a job, and the last two steps of both walks.",
    steps: [
      { act: "Let it add the total up.", shot: "walk:a7-bill",
        why: "It is totaled from the lines on the server and nobody types it, so it cannot disagree with the figure the customer approved. An authorization is worthless if the number on it can be typed by the person it binds." },
      { act: "Know that raising the invoice sends nothing.",
        why: "It is the bill existing so it can be paid. It also will not bill work that is not finished, and it says so rather than quietly doing it." },
      { act: "Take part of it when that is what they are paying.", shot: "walk:a8-money",
        why: "Type a smaller figure for a deposit or for just the diagnostic and it tells you what is still owed. A deposit does not close the job." },
      { act: "Take a pre-purchase inspection's money before it is done.", engineOnly: true,
        why: "It is a Prepayment, on its own card, open on the Money tab. Approving it on their own page puts a Pay button in front of them; a Zelle you record goes on the same Prepayment. Neither closes the job. When the inspection is done the bill nets it off and closes at nothing owed. Whoever takes it first is its advisor, and the MechanicRank desk sees that." },
      { act: "Let the payment close the job.",
        why: "A settled invoice settles the job by itself. Nobody has to remember to move it, which is the commonest way a finished job sits unbilled for a fortnight." },
      { act: "If a payment goes on twice, take it off with a reason.",
        why: "It is struck through rather than deleted and the job comes back out of paid on its own. The record of the correction is part of the record." },
      { act: "Take a card at the counter.", go: "#/terminal",
        why: "Pick the invoice, key the card, done. The card is turned into a one time code in that browser, so neither your screen nor the server ever holds a card number. It refuses an invoice that is already settled and a job with no invoice at all, because both of those are how a shop charges somebody twice." },
      { act: "Work the owed list instead of your memory.", go: "#/invoices", shot: "owed",
        why: "It opens on what is owed, because an open invoice is a question and a paid one is only a receipt. Anything past a fortnight wants a phone call, not another email." },
      { act: "Put your hours on the job.", go: "#/pay", shot: "payroll",
        why: "That is what payroll reads. A job with no hours on it is a job nobody was paid for, and nobody finds out until payday." },
      { act: "Use one screen with four views, not four screens.",
        why: "The pay run, hours, payouts and your own pay are four views of earned minus paid. Keeping them apart is exactly what makes somebody read hours on one screen, money on another, and do the sum in their head." },
      { act: "Read the numbers off the money.", go: "#/reports",
        why: "Revenue, leads and conversion are counted from invoices and payments, never from what a job status says. A status is a claim. A payment is a fact." },
    ],
    traps: [
      "Card payments need the shop on its own gateway, which is a short setup call. Until then the desk offers cash, check and transfer, and it refuses to bill a card against another shop's account on purpose.",
      "A storage charge or a fee recorded as labor pays commission on it. Record fees as fees.",
      "A {tech} sees their own pay and nobody else's rate. Pay run, hours and payouts are kept to whoever runs the shop.",
      "Cash written up properly is the difference between a real revenue figure and a guess.",
    ],
  },
  {
    key: "portal", role: "The customer's own page", who: "What they see from the driveway",
    icon: "customers", tint: "green", group: "The screens", roles: EVERYONE,
    opens: "One text. No app and no password, and it is the cheapest way there is to stop the {whereismycall} call.",
    steps: [
      { act: "Send them their page early, not when they chase.", shot: "feed",
        why: "One link opens the stage the job is at, the photos your {tech} took, the price before the work starts and the receipt after. A customer who can see photographs does not ring to ask how it is going." },
      { act: "They approve it there, with a finger.",
        why: "They read what is on the quote line by line, they tap yes and they sign on their own screen. One signature covers the price and the work, and the page tells them that before they sign." },
      { act: "They can pay from it too.",
        why: "The same form the counter terminal uses, so there is one way money comes in and two doors onto it rather than two systems." },
      { act: "Know exactly what does not cross.",
        why: "Notes written for the customer cross. Internal notes never do. Receipt photos never do, because they carry what the shop paid. The total appears only once the job has been billed or paid." },
      { act: "Treat the link as a key to one job.",
        why: "A real link with somebody else's job on the end of it is a not found, never a peek at another customer." },
      { act: "Use the fleet version for an office manager.",
        why: "Every {fleetunit} and every job on one page, instead of a separate link per {thing}." },
    ],
    traps: [
      "Sensitive details are blurred on their page until they ask for them.",
      "Sending the page is not the same as sending the quote. The quote send carries the link and starts the thirty day clock. Sending the link on its own does not.",
      "If the number on the record is wrong, the page goes to the wrong person. That is why only the desk can change a phone number.",
    ],
  },
  /**
   * HOW TO CHANGE THINGS. Anthony: "show all parts of the shop desk system
   * and how to make changes." Everything here is a setting that something
   * else reads, so each step says what reads it.
   */
  {
    key: "settings", role: "Setting the shop up", who: "You change how it behaves",
    group: "Start here",
    icon: "settings", tint: "purple", roles: RUNS_IT,
    opens: "Settings. Almost nothing here is cosmetic -- each field is read by something that runs on its own.",
    steps: [
      { act: "Set the labor rate.", go: "#/settings",
        why: "Every quote raised from then on prices its labor off this number. Changing it does not reprice quotes already out, which is deliberate. A price you sent is a price you sent." },
      { act: "Put the shop's address in the shop profile.", go: "#/settings",
        why: "Any job marked In the shop uses it, and the customer gets it on their own page. There is no address field on an in-shop job any more -- it is written once, here." },
      { act: "Set the hours, and the terms URL.", go: "#/settings",
        why: "Hours drive the day and night textback wording. The terms link goes on every quote the customer approves, so it is part of the authorization." },
      { act: "Add people on Team, and give each one a role.", go: "#/team",
        why: "The role is not a label. It decides which fields they may edit, which walkthroughs they see, and which of the two guided walks they get. A {tech} cannot change a phone number. An advisor can.",
        shot: "team-score" },
      { act: "Take a login away the day somebody leaves.", go: "#/team",
        why: "A person who left still holding a login is the most ordinary way a shop gets robbed." },
      { act: "Check the shop's own text number.", go: "#/settings",
        why: "Every shop on the system has its own number. Outbound texts go from it and inbound texts are filed by it, so a wrong number here silently sends your customers to somebody else's inbox." },
      { act: "Say what trade you are in.", go: "#/settings",
        why: "It changes the words the whole app uses. A garage reads Vehicles, a garage door shop reads Doors, a locksmith reads Locks and an investigator reads Cases, on the nav, on the job card and in both guided walks. The web address never changes with it, because a link somebody bookmarked is not copy." },
      { act: "Put your logo and your color on it.", go: "#/settings",
        why: "The top bar, the branded front page and the customer's own page all follow the shop record, so this is one setting rather than a rebuild. On your own domain your customers never see anybody else's brand." },
      { act: "Set your tax rate if you charge one.", go: "#/settings",
        why: "It adds an automatic line to every quote, worked out on everything charged, and you can still edit it on the job. Set it to zero to turn it off." },
      { act: "Turn automations on and off where you can see them all at once.", go: "#/automations", shot: "autoswitch",
        why: "The nudge, the quote sweep, the aged-lead close, the review ask. Each one has a row and a switch, and an automation with no row is off no matter what the code says." },
      { act: "Set the payment methods you actually take.", go: "#/billing",
        why: "This is what the customer sees on their own page. Offering a method the shop cannot settle is a phone call you did not need." },
    ],
    traps: [
      "Changing the labor rate does not reprice anything already quoted. If you meant to reprice, open the quote and change the line.",
      "A role change takes effect the next time that person loads the app, not mid-session.",
      "Automations run whether or not anybody is looking. Turning one on at 5pm means it may fire at 6.",
    ],
  },
  /**
   * TALKING TO PEOPLE. Every channel in one place, because the commonest
   * mistake is saying something twice on two of them.
   */
  {
    key: "comms", role: "Talking to customers", who: "You own what gets said",
    group: "The screens",
    icon: "comms", tint: "green", roles: DESK.concat(["technician"]),
    opens: "The job's own conversation. Everything said to a customer lands on the job, whichever way it went out.",
    steps: [
      { act: "Text from the app, never from your own phone.",
        why: "A text sent from the CRM lands on the job, on the customer and in the feed. One sent from your pocket exists nowhere, and the next person to pick {thatcar} up has no idea it happened.",
        shot: "feed" },
      { act: "Let the opener answer a missed call.",
        why: "A missed call gets a text back within a minute, day or night, in the shop's words. It is already on. You do not need to also text them." },
      { act: "Take a thread over by replying in it.",
        why: "The moment a person types, the automation stops talking on that thread. That is what stops the shop and the robot answering the same question two minutes apart." },
      { act: "Send the customer their own page.",
        why: "Milestones, photos and the approve button. It is the cheapest way to stop the {whereismy} call, and sensitive details on it are blurred until they ask." },
      { act: "Check what actually went out before chasing.",
        why: "A sent quote chases itself once a day for four days and stops the moment they say anything at all. Chasing by hand on top of that sends them two." },
      /* Added 2026-09-19, the day the answer line replied to a real
         prospect and his phone never showed it. Until then nothing in the
         product could tell a text that arrived from one that did not, so
         the manual could not honestly say this. It can now, and a person
         who does not know to look at the pill is back where we started. */
      { act: "Believe the receipt on the thread, not the fact that you pressed send.",
        why: "Sent and delivered are two different things, and carriers drop texts silently. Every one we send is read back, so a message that never reached them is marked NOT DELIVERED in red on the thread and in the list beside it. Ring that person: they have not seen a word of it." },
      { act: "{Techs}: use the Text Desk number, not the customer's thread.",
        why: "Texting the desk number files photos, hours and questions against the job you are on. It is for you and the shop, and nothing you send it reaches the customer." },
      { act: "Know what the answer line does before you hand out the number.",
        engineOnly: true,
        why: "It answers car questions from a text. Text TECH first and it talks specs, tests and codes with no limits and no upsell -- that is the line for a working mechanic, not a driver." },
    ],
    traps: [
      "A customer with no record on file cannot be texted from the lead sheet. Make the customer first, then text.",
      "Two people replying to the same thread from two screens is the one thing this cannot untangle for you. The feed shows who spoke last.",
      "Ringing still happens from your own phone. Only texts and emails go through the app.",
    ],
  },
  /**
   * ─── THE TWO NOBODY READS UNTIL THEY NEED THEM ─────────────────────────
   *
   * THE RULES, because they are the only part of this product that does not
   * bend for a role, and a person who runs into one without knowing it
   * exists reads it as the software being broken. Written as a track rather
   * than scattered through the others so there is one place to point at.
   *
   * WHEN SOMETHING GOES WRONG, because the alternative is a phone call. The
   * shop rang once with a black screen and could not log in, and the fix
   * was one button that nobody had ever been shown. Every entry here is a
   * real support call this shop has taken.
   */
  {
    key: "rules", role: "The rules that do not bend", who: "They apply to everybody",
    icon: "authorizations", tint: "purple", group: "When it matters", roles: EVERYONE,
    opens: "Everywhere. These are the shop's obligations, and they hold for whoever is holding the phone, the owner included.",
    steps: [
      { act: "Nothing is booked before there is a yes.", shot: "blocked",
        why: "Lead, estimate, approval, job, scheduled, in that order. Approving records the approval and never touches the date. One path sets the status and the date together, and that is booking it in." },
      { act: "No authorization, no start.",
        why: "Signed or spoken, it has to exist before the work does. The {tech} walk offers to write a spoken yes down on the spot rather than letting anybody past it." },
      { act: "No bill before the work is finished.",
        why: "The bill step says the work is not finished yet rather than raising an invoice for something nobody has done." },
      { act: "Nothing closes on a {thing} nobody can identify.",
        why: "{identify} That means not completed, not invoiced and not paid. A job on a {thing} nobody can identify cannot be warrantied, looked up, or defended if somebody asks about it a year later." },
      { act: "The total is added up, never typed.",
        why: "It comes off the lines on the server. An authorization is worthless if the figure on it can be typed by the person it binds." },
      { act: "One signature, one ask.",
        why: "It approves the figure and it authorizes the listed work, and the customer is told that above the pad before they sign. The old system asked one customer four times." },
      { act: "Over the approved figure means stop.",
        why: "A new quote, sent, and a new yes at the new figure before a wrench moves. A second yes at a revised figure is expected. A second one at the same figure is refused, because two prices is the whole reason a second answer is needed." },
      { act: "An override is not a bypass.",
        why: "An advisor, an admin or a supervisor can close a job that failed its checks. The rule stays standing, a reason is required, and both the name and the reason go on the job for good. A technician does not get the button, and that is not about trust. Being walked through what is missing IS the check working." },
    ],
    traps: [
      "There is no setting that switches these off. That is exactly what makes them worth having.",
      "An override should stay rare. The difference between no record at all and a named person with a written reason on the job is the difference between a problem and a note.",
      "If a button refuses you, the line underneath says what has to happen first. It is not broken and it is not nagging.",
    ],
  },
  {
    key: "fixit", role: "When something goes wrong", who: "Read this before you ring anybody",
    icon: "more", tint: "amber", group: "When it matters", roles: EVERYONE,
    opens: "Anywhere. Almost everything on this list is two taps.",
    steps: [
      { act: "A blank or black screen is the phone, not the shop.", shot: "stopped",
        why: "It is almost always a saved sign in that cannot be read any more. Press Clear this device and start again. Nothing is lost, because nothing is kept on the phone. It is all on the server." },
      { act: "There is a link you can text to somebody stuck.",
        why: "Put ?reset=1 on the end of the sign in address and it clears that device on its own, so a person on the phone to a stuck technician has one link to send rather than a walk through browser settings." },
      { act: "Every list empty means no shop, not no data.",
        why: "Signed in and not attached to a shop yet. The database answers every question with nothing, which looks exactly like a broken screen. Whoever runs the shop adds them on Team and it fills in." },
      { act: "A button that does nothing is a step that is blocked.", shot: "blocked",
        why: "Read the line under it. It names what has to happen first, and the walk will skip you to the first thing you actually can do." },
      { act: "A quote that will not send usually has no mobile number.",
        why: "The step says so before you tap it rather than failing on the tap. Add the number on the customer and send again." },
      { act: "A job that will not close is missing the {thing} or the finish.",
        why: "Either nothing identifies it yet, or nobody has marked the work finished. The screen names which." },
      { act: "Texts going nowhere is two settings, in this order.", go: "#/settings",
        why: "First the shop's own text number in Settings, because a wrong one sends your customers to somebody else's inbox. Then the switch on Automations, because an automation with no row is off no matter what anything else says." },
      { act: "An automation that reads on and answers nobody is rehearsing.", go: "#/automations", shot: "autoswitch",
        why: "While it is holding a forward number every reply goes to that number and the person who texted in hears nothing at all. The row says so out loud, because a switch that reads on either way is the most dangerous thing on a screen." },
      { act: "A price that looks wrong is usually the labor rate.", go: "#/settings",
        why: "Every quote prices its labor off that one number, and a quote already sent keeps the price it was sent at." },
      { act: "Still stuck, ring the shop on (951) 904-2477.",
        why: "Whoever answers runs a repair shop and has had the same bad morning you are having." },
    ],
    traps: [
      "Clearing a device loses nothing. Everything is on the server, which is why the button is safe to press on somebody else's phone.",
      "Two people replying to one thread from two screens is the one thing this cannot untangle for you. The feed shows who spoke last.",
      "A change somebody made may not be showing for you because you have not reloaded. A role change takes effect the next time the app loads, never mid session.",
    ],
  },
];

/** The tiles this person is meant to see. */
export function walkthroughsPage(role: Role = "viewer"): void {
  const mine = tracksFor(role);
  /* Promoted in the order a shop is run rather than the order they were
     written, and filtered by what this person is actually allowed, so a
     technician sees one card rather than three with two that refuse him. */
  const roles = ROLE_TRACKS
    .map((k) => mine.find((t) => t.key === k))
    .filter((t): t is Track => Boolean(t));
  const promoted = new Set(roles.map((t) => t.key));

  const roleCard = (t: Track) => `
    <a class="wt-rolecard ${esc(t.tint)}${HERO[t.key] ? "" : " nopic"}"
       href="#/walkthrough/${esc(t.key)}">
      ${HERO[t.key] ? `<span class="wt-rolepic">
        <img loading="lazy" src="/images/walk/phone/${esc(HERO[t.key] ?? "")}.webp"
             alt="The ${esc(say(t.role))} screen on a phone"></span>` : ""}
      <span class="wt-rolebody">
        <span class="wt-roleico">${icon(t.icon)}</span>
        <span class="wt-roletitle">${esc(say(t.role))}</span>
        <span class="wt-rolewho">${esc(say(t.who))}</span>
        <span class="wt-rolen">${t.steps.length} steps</span>
      </span>
    </a>`;

  mount(`<div class="page">
    <header class="page-head"><h1>Walkthroughs</h1>
      <p>The whole system, in the order you meet it. ${mine.length === 1
        ? "This one is yours."
        : `${mine.length} of these are yours. The rest are not shown.`}</p></header>

    ${roles.length ? `
      <h2 class="wt-group">${roles.length === 1 ? "Your job" : "Start with your job"}</h2>
      <div class="wt-roles">${roles.map(roleCard).join("")}</div>` : ""}

    <a class="wt-wide" href="#/workflow">
      <span class="wt-wideico">${icon("desk")}</span>
      <span class="wt-widebody">
        <span class="wt-widetitle">The job, end to end</span>
        <span class="wt-widewho">All eighteen steps from the first call to the keys going
          back, with the map and the photos from a real job</span>
      </span>
      <span class="wt-widego">18 steps &rarr;</span>
    </a>

    ${SHELVES.map((shelf) => {
      /* A shelf with nothing left on it for this person is not an empty
         heading. It is not drawn at all -- a viewer seeing four headings
         and two tiles learns the manual is mostly not for them, which is
         the thing role filtering exists to prevent. */
      const on = mine.filter((t) => t.group === shelf && !promoted.has(t.key));
      if (!on.length) return "";
      return `<h2 class="wt-group">${esc(shelf)}</h2>
        <div class="wt-tiles">
          ${on.map((t) => `
            <a class="wt-tile ${esc(t.tint)}" href="#/walkthrough/${esc(t.key)}">
              <span class="wt-ico">${icon(t.icon)}</span>
              <div class="wt-role">${esc(say(t.role))}</div>
              <div class="wt-who">${esc(say(t.who))}</div>
              <div class="wt-n">${t.steps.length} steps</div>
            </a>`).join("")}
        </div>`;
    }).join("")}
    <p class="quiet" style="text-align:center">
      Written for the screen in front of you. Every step links to where it happens.</p>
  </div>`);
}

/** One track, opened. */
export function walkthroughPage(key: string, role: Role = "viewer"): void {
  const t = TRACKS.find((x) => x.key === key);
  /* A track that is not this person's job sends them back to their own
     tiles rather than showing them somebody else's manual. */
  if (!t || !t.roles.includes(role)) return walkthroughsPage(role);

  const anyShots = t.steps.some((s) => s.shot);

  mount(`<div class="page">
    <a class="back" href="#/walkthroughs">&larr; All walkthroughs</a>

    <!-- The same picture the card carried, so arriving here confirms you
         opened what you meant to. A track with no picture gets the tint on
         its own, which reads as a header rather than as a hole. -->
    <header class="wt-hero ${esc(t.tint)}">
      <div class="wt-herotext">
        <span class="wt-heroico">${icon(t.icon)}</span>
        <h1>${esc(say(t.role))}</h1>
        <p>${esc(say(t.who))}. ${esc(say(t.opens))}</p>
        <span class="wt-heron">${t.steps.length} steps</span>
      </div>
      ${HERO[t.key] ? `<div class="wt-heropic">
        <img loading="lazy" src="/images/walk/phone/${esc(HERO[t.key] ?? "")}.webp"
             alt="The ${esc(say(t.role))} screen on a phone"></div>` : ""}
    </header>

    ${anyShots ? `<div class="wt-devbar"><span class="wt-devnote">
        Pictures of the screen you are on</span>
      <div class="crewbar" id="wt-dev">${DEVICES.map((d) =>
        `<button class="crewpill${d.tag === device ? " on" : ""}"
           data-d="${esc(d.tag || "d")}">${esc(d.label)}</button>`).join("")}</div>
    </div>` : ""}

    <ol class="wt-steps ${esc(t.tint)}">
      ${t.steps.filter((s) => !s.engineOnly || shopWords().hasEngine).map((s, i) => `
        <li>
          <span class="wt-num">${i + 1}</span>
          <div class="wt-body">
            <div class="wt-do">${esc(say(s.act))}</div>
            ${s.why ? `<div class="wt-why">${esc(say(s.why))}</div>` : ""}
            ${s.shot ? shotOf(esc(s.shot), esc(say(s.act))) : ""}
            ${s.go ? `<a class="wt-go" href="${esc(s.go)}">Take me there &rarr;</a>` : ""}
          </div>
        </li>`).join("")}
    </ol>

    <div class="card">
      <div class="card-head">Things that catch people out</div>
      ${t.traps.map((x) => `<div class="wt-trap">${esc(say(x))}</div>`).join("")}
    </div>
  </div>`);

  /* Swapping the device redraws the whole track, which is one function
     call and no state to keep in sync. The choice sticks for the session
     because somebody training on a phone stays on a phone. */
  document.getElementById("wt-dev")?.querySelectorAll<HTMLButtonElement>("[data-d]")
    .forEach((b) => b.addEventListener("click", () => {
      const d = b.dataset["d"] ?? "d";
      device = (d === "d" ? "" : d) as Device;
      walkthroughPage(key, role);
      window.scrollTo({ top: 0 });
    }));
}
